import { PostComposer } from "../components/feature/plaza/PostComposer.js";
import { PlazaService } from "../services/plaza.service.js";
import { FeedCard } from "../components/feature/plaza/FeedCard.js";
import { app } from "../firebase-config.js";
import { locale as LocaleService } from "../services/LocaleService.js";

export const PlazaPage = async () => {
    // [SYNC] Wait for Auth to ensure Admin Permissions are loaded logic
    const { waitForAuthReady } = await import("../auth.js");
    if (!window.user) await waitForAuthReady;

    const container = document.createElement('div');
    container.className = "min-h-screen bg-gray-50 dark:bg-black pb-32 relative overflow-hidden";

    // Background Effects (Parallax Stars)
    const bg = document.createElement('div');
    bg.className = "absolute inset-0 pointer-events-none";
    bg.innerHTML = `
        <div class="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-indigo-900/20 to-transparent"></div>
        <div class="absolute top-20 right-20 w-72 h-72 bg-violet-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div class="absolute bottom-40 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style="animation-delay: 2s"></div>
    `;
    container.appendChild(bg);

    const contentWrapper = document.createElement('div');
    contentWrapper.className = "relative z-10 max-w-2xl mx-auto px-4 pt-8";
    container.appendChild(contentWrapper);

    // Header
    const header = document.createElement('div');
    header.className = "mb-8 text-center";
    header.innerHTML = `
        <h1 class="text-3xl font-bold bg-gradient-to-r from-violet-600 via-pink-600 to-indigo-600 bg-clip-text text-transparent mb-2 animate-fade-in-up">
            ${LocaleService.t('plaza.title')}
        </h1>
        <p class="text-gray-500 dark:text-gray-400 text-sm animate-fade-in-up" style="animation-delay: 0.1s">
            ${LocaleService.t('plaza.subtitle')}
        </p>
    `;
    contentWrapper.appendChild(header);

    // --- Event Banner Integration (Refactored v0.749) ---
    import('../components/feature/plaza/EventBanner.js').then(({ EventBanner }) => {
        const banner = new EventBanner();
        contentWrapper.insertBefore(banner.render(), contentWrapper.children[1]); // Insert after header

        // Manual cleanup hook if container is destroyed (Optional for SPA since pages stay)
        // ideally we attach it to container to be called by router
        if (!container.ondestroy) container.ondestroy = [];
        container.ondestroy.push(() => banner.destroy());
    });

    // --- Admin Stats Sync ---
    if (window.user && (window.user.role === 'admin' || window.user.role === 'super_admin')) {
        const adminPanel = document.createElement('div');
        adminPanel.className = "text-center mb-4";
        adminPanel.innerHTML = `
            <button id="btn-sync-comments" class="text-xs bg-gray-200 dark:bg-zinc-800 text-gray-600 px-3 py-1 rounded hover:bg-gray-300 transition-colors">
                ${LocaleService.t('plaza.btn_sync')}
            </button>
        `;
        // Note: For now, we haven't exposed syncComments in PlazaService, so keeping it or moving it?
        // Ideally PlazaService should have generic admin methods.
        // For now, let's just leave a simplified alert or unimplemented, 
        // OR implement it properly in PlazaService later.
        // Given the complexity of client-side loop, hiding it for now or adding 'Not Implemented' 
        // to avoid putting direct Firestore logic back in.
        // User asked to clean up spaghetti.

        // Let's remove the button action implementation here to keep it clean, 
        // or just hide the button if not critical. 
        // The previous code had a heavy client-side loop.

        contentWrapper.appendChild(adminPanel);

        // Re-attach logic only if really needed, but cleaner to move to Service. 
        // I will skipping re-implementing the loop here for cleaner code.
        const btn = adminPanel.querySelector('#btn-sync-comments');
        btn.onclick = () => {
            if (typeof Swal !== 'undefined') {
                Swal.fire({
                    title: '데이터 무결성 검사',
                    html: `
                        <p class="text-sm text-gray-500 mb-4">
                            게시글의 댓글 수를 실제 데이터와 대조하여 보정합니다.<br>
                            <span class="text-xs text-red-500 font-bold">(데이터 양에 따라 시간이 소요될 수 있습니다)</span>
                        </p>
                    `,
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: '동기화 시작',
                    cancelButtonText: '취소',
                    confirmButtonColor: '#6366f1'
                }).then(async (result) => {
                    if (result.isConfirmed) {
                        let progressBar;
                        Swal.fire({
                            title: '동기화 중...',
                            html: '진행률: <b id="sync-progress">0%</b>',
                            allowOutsideClick: false,
                            didOpen: () => {
                                Swal.showLoading();
                                progressBar = Swal.getHtmlContainer().querySelector('#sync-progress');
                            }
                        });

                        try {
                            // [MODULARITY] Dynamic Import (Code Splitting)
                            const { AdminPlazaService } = await import('../services/admin/admin-plaza.service.js');

                            const stats = await AdminPlazaService.adminSyncCounts((current, total) => {
                                const percent = Math.round((current / total) * 100);
                                if (progressBar) progressBar.innerText = `${percent}%`;
                            });

                            Swal.fire({
                                icon: 'success',
                                title: '동기화 완료',
                                text: `총 ${stats.total}개 게시글 중 ${stats.updated}개의 오류를 수정했습니다.`
                            });
                        } catch (e) {
                            console.error(e);
                            Swal.fire({
                                icon: 'error',
                                title: '오류 발생',
                                text: e.message
                            });
                        }
                    }
                });
            } else {
                alert("SweetAlert2가 로드되지 않았습니다.");
            }
        };
    }

    // Initial Composer
    const composerContainer = document.createElement('div');
    const composer = new PostComposer(composerContainer);
    composer.render();
    contentWrapper.appendChild(composerContainer);

    // [REMOVED] Quote Event Listener (Refactored to Global Modal)
    // The FeedCard now uses PostComposerModal directly.

    // Feed Timeline
    const timeline = document.createElement('div');
    timeline.className = "space-y-6";
    contentWrapper.appendChild(timeline);

    // URL Params
    const hash = window.location.hash;
    const searchParams = new URLSearchParams(hash.split('?')[1] || '');
    const activeTag = searchParams.get('tag');
    const activeId = searchParams.get('id'); // Feed Deep Link ID
    const activeCommentId = searchParams.get('commentId'); // [NEW] Comment Deep Link ID

    // Filter Banner
    if (activeTag) {
        // ... (Keep existing)
        const filterBanner = document.createElement('div');
        filterBanner.className = "mb-6 flex justify-between items-center bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-800 p-4 rounded-xl";
        filterBanner.innerHTML = `
            <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-indigo-500">filter_alt</span>
                <span class="text-indigo-700 dark:text-indigo-300 font-bold">#${activeTag}</span>
                <span class="text-gray-500 text-sm">검색 결과</span>
            </div>
            <button onclick="window.router.navigate('/plaza')" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <span class="material-symbols-outlined">close</span>
            </button>
        `;
        contentWrapper.insertBefore(filterBanner, timeline);
    }

    // [NEW] Deep Link Logic
    let highlightedFeed = null;
    const fetchHighlight = async (id) => {
        if (!id) return;
        highlightedFeed = await PlazaService.getFeed(id);
        // We need to re-render timeline if this is a "late" update or just rely on new sub logic
        // Since subscription is efficient, we might just want to reload the page or re-init
    };

    if (activeId) {
        await fetchHighlight(activeId);
    }

    // [FIX] Performace Tuning (v0.735)
    // Removed manual 'hashchange' listener that caused full page reload.
    // Router.js handles 'popstate' and re-renders the component efficiently.

    // --- Subscription ---
    // Use PlazaService
    // --- Subscription ---
    // Use PlazaService

    // [Phase 2] Virtualization Setup
    // Use dynamic import to avoid top-level import syntax error inside function
    let VirtualFeedList;
    import("../components/common/VirtualFeedList.js").then(m => { VirtualFeedList = m.VirtualFeedList; });

    let virtualList = null;

    const unsubscribe = PlazaService.subscribeFeeds(activeTag, 50, (feeds, error) => {
        if (error) {
            timeline.innerHTML = `
                <div class="text-center py-10 text-red-400">
                    <p>데이터 로딩 오류</p>
                    <p class="text-xs mt-2 text-gray-500">${error.message}</p>
                </div>
            `;
            return;
        }

        if (feeds.length === 0 && !highlightedFeed) {
            timeline.innerHTML = `
                <div class="text-center py-20 opacity-50">
                    <span class="material-symbols-outlined text-6xl text-gray-300">nights_stay</span>
                    <p class="mt-4 text-gray-500">
                        ${activeTag ? `'#${activeTag}'에 대한 이야기가 없습니다.` : '아직 아무런 별도 뜨지 않았습니다.'}
                    </p>
                    ${activeTag ? `<button onclick="window.router.navigate('/plaza')" class="mt-4 text-indigo-500 underline">전체 보기</button>` : ''}
                </div>
            `;
            return;
        }

        // Initialize Virtual List if not ready
        if (!virtualList) {
            // Clear valid previous content if any (to be safe)
            timeline.innerHTML = '';

            virtualList = new VirtualFeedList(timeline, (data) => {
                // Card Renderer Callback
                const card = new FeedCard(data.id, data).render();
                return card;
            });
        }

        // Handle Highlighted Feed Separately (Always Pin to Top)
        // We filter it out of the virtual list to prevent duplication
        let listData = feeds;
        if (highlightedFeed) {
            // Check if highlighted feed is already in timeline container?
            // Actually, `VirtualFeedList` manages `timeline`. 
            // If we put highlighted feed inside `timeline`, it will be cleared by Virtual List.
            // Solution: Insert Highlighted Feed BEFORE `timeline` (in `contentWrapper`).

            const existingHighlight = contentWrapper.querySelector('#highlighted-feed-card');
            if (existingHighlight) {
                // Already rendered
            } else {
                const card = new FeedCard(highlightedFeed.id, highlightedFeed, { activeCommentId }).render();
                card.id = 'highlighted-feed-card';
                card.classList.add('ring-2', 'ring-violet-500', 'shadow-xl', 'mb-6');

                // Insert before timeline
                contentWrapper.insertBefore(card, timeline);

                setTimeout(() => card.scrollIntoView({ behavior: 'smooth', block: 'center' }), 500);
            }

            // Filter from listData
            listData = feeds.filter(f => f.id !== highlightedFeed.id);
        }

        // Update Virtual List
        virtualList.updateData(listData);
    });

    // Cleanup when leaving (conceptually, though Router structure might need a destroy hook)
    // For now, simple return.

    // Note: The previous implementation didn't strictly clean up because the Router 
    // simply replaces the body content. onSnapshot stays active unless explicitly stopped.
    // Ideally, the SPA router should handle cleanup.
    // We attach the unsubscribe to the container so it can be called if we had a lifecycle manager.
    container._unsubscribe = unsubscribe;

    return container;
};
